import styled from "@emotion/styled";
import { Instant } from "@js-joda/core";
import React from "react";
import useInstantCountdown from "../../../hooks/useInstantCountdown";
import { CaregiverId, RaffleParticipationStatus } from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { fmap } from "../../../utils/fmap";
import AppSlideFadeIn from "../../Generic/AppSlideFadeIn";
import { RaffleParticipant } from "../Raffle.types";
import RaffleDynamicWinnerSelector from "./RaffleDynamicWinnerSelector";
import RaffleHeading from "./RaffleHeading";
import RaffleParticipationStatusCard from "./RaffleParticipationStatusCard";
import { RaffleSection } from "./RaffleSection";

type Props = {
  raffleEndsAt: Instant;
  participants: RaffleParticipant[];
  winnerCaregiverId: CaregiverId | null;
  tokens: number;
  participationStatus: RaffleParticipationStatus;
  avatarUrl: string | null;
  onTimeToChooseWinner: () => void;
};

export default function RaffleScreenOngoing(props: Props) {
  const [winnerChosen, setWinnerChosen] = React.useState(false);
  const [startedSpinning, setStartedSpinning] = React.useState(false);
  const { formatted: countdown, totalSeconds } = useInstantCountdown({
    targetInstant: props.raffleEndsAt,
    biggestUnit: "minutes",
    onTick: ({ totalSeconds }) => {
      if (totalSeconds > 30 || props.winnerCaregiverId !== null) return;
      props.onTimeToChooseWinner();
    },
  });
  const winnerParticipantIndex = fmap(props.winnerCaregiverId, (winnerCaregiverId) =>
    props.participants.findIndex((participant) => participant.caregiverId === winnerCaregiverId)
  );
  const winnerParticipant = fmap(winnerParticipantIndex, (i) => props.participants[i]);

  return (
    <OngoingScreenRoot>
      <AppSlideFadeIn>
        <RaffleSection mt={72}>
          {winnerChosen && winnerParticipant !== null ? (
            <RaffleHeading
              subTitle={translate(STRINGS.CONGRATULATIONS_TO)}
              title={winnerParticipant.name}
            />
          ) : startedSpinning ? (
            <RaffleHeading
              subTitle={translate(STRINGS.CHOOSING_A_WINNER)}
              title={translate(STRINGS.RAFFLE_NOW)}
            />
          ) : (
            <RaffleHeading subTitle={translate(STRINGS.CHOOSING_A_WINNER_IN)} title={countdown} />
          )}
        </RaffleSection>
      </AppSlideFadeIn>

      <AppSlideFadeIn delay={100}>
        <RaffleDynamicWinnerSelector
          participants={props.participants}
          spinPayload={
            winnerParticipantIndex === null
              ? null
              : {
                  active: totalSeconds === 0,
                  winnerParticipantIndex: winnerParticipantIndex,
                }
          }
          showConfetti={winnerChosen}
          animatePulse={startedSpinning}
          raffleEndsAt={props.raffleEndsAt}
          onWinAnimationEnd={() => setWinnerChosen(true)}
          onSpinIteration={() => totalSeconds === 0 && setStartedSpinning(true)}
        />
      </AppSlideFadeIn>

      <AppSlideFadeIn delay={200}>
        <RaffleSection mt={72}>
          <RaffleParticipationStatusCard
            avatarUrl={props.avatarUrl}
            isParticipating={props.participationStatus.isParticipating}
          />
        </RaffleSection>
      </AppSlideFadeIn>
    </OngoingScreenRoot>
  );
}

const OngoingScreenRoot = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  overflow-x: hidden;
  height: 100%;
`;
